import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useLocation, useParams } from "react-router-dom";
import { useSwipeable } from 'react-swipeable';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Icon } from 'semantic-ui-react';
import { PickPeekModal } from '../../components/pick-peek-modal/pick-peek-modal';
import { status } from '../../configs/status';
import { selectLeague } from '../../controller/league/leagueSlice';
import { publish, PubSub } from '../../controller/pubSub/pubSubSlice';
import { selectUser } from '../../controller/user/userSlice';
import { fetchWeek, getSetWeek, selectPicksCount } from '../../controller/week/weekSlice';
import { SeasonRequest } from '../../model/postRequests/seasonRequest';
import { UserTypeEnum } from '../../model/user/user';
import { GameStatusEnum } from '../../model/week/game';
import { RootState } from '../../store';
import "../../utils/slideTransition.scss";
import { toInt } from '../../utils/tools';
import { GamesTabBar } from './components/games-tab-bar/games-tab-bar';
import { WeekSwitcher } from './components/week-switcher/week-switcher';
import "./games.css";

interface RouteParams {
    view: string
}

type Props = {
    routes: {path: string, component: any}[] | undefined
}

export const Games = ({routes}: Props) => {
    const user = useSelector(selectUser);
    const league = useSelector(selectLeague);
    
    const gamesState = useSelector((state: RootState) => state.week.status);
    const leagueState = useSelector((state: RootState) => state.league.status);
    const setWeek = useSelector(getSetWeek);
    const pickCount = useSelector(selectPicksCount);
    const dispatch = useDispatch();

    let location = useLocation();
    let history = useHistory();
    const animationTime = { enter: 200, exit: 200};
    let param = useParams<RouteParams>();
    let { search } = useLocation();
    const query = new URLSearchParams(search);
    const season = toInt(query.get("season"));
    const week = toInt(query.get("week"));
    const seasonType = toInt(query.get("seasonType"))
    const other = query.get("user");

    const currSeason = league.currentSeason
    const currWeek = league.currentWeek 
    const currSeasonType = league.currentSeasonType

    const filterList = [
        {id: GameStatusEnum.completed, label: 'Completed'},
        {id: GameStatusEnum.live, label: 'Live'},
        {id: GameStatusEnum.unplayed, label: 'Unplayed'}
    ] as Array<{id: string, label: string}>

    const swipeView = (view: string) => {
        if(season === null) {
            history.push(`/games/${view}`);
        } else {
            history.push(`/games/${view}?season=${season}&seasonType=${seasonType}&week=${week}`);
        }
    }

    const swipeHandlers = useSwipeable({
        onSwipedRight: () => swipeView('game'),
        onSwipedLeft: () => swipeView('pick'),
        delta : 100,
    });

    const tabBarEvent = (publishEvent: PubSub) => {
        dispatch(publish(publishEvent));
    }

    const spectatorView = (user.current_season_data.user_type !== UserTypeEnum.PARTICIPANT) && (
        <div className="header-container">
            <div className="spectator-container warn-background">
                <div className="base-color spectator-icon">
                    <Icon name="binoculars" />
                </div>
                <span className="base-color">
                    Spectator Mode
                </span>
            </div>
        </div>
    );

    const gamesTab = (user.current_season_data.user_type === UserTypeEnum.PARTICIPANT && (other === null || other === "null")) && (
        <GamesTabBar
            league={league}
            pickCount={pickCount}
            tabBarEvent={tabBarEvent}
        />
    )

    const transitionGroup = (
        <TransitionGroup component="div" className="route-container">
            <CSSTransition 
                key={param.view} 
                timeout={animationTime} 
                classNames="pageSlider" 
                mountOnEnter={false} 
                unmountOnExit={true}>
                <div className={param.view === "pick" ? "left" : "right"}>
                    <Switch location={location}>
                        {(routes) && routes.map((route, i) =>(
                            <Route
                                key={i}
                                path={route.path}
                                render={props => (<route.component key={i} />)}
                            />
                        ))}
                    </Switch>
                </div>
            </CSSTransition>
        </TransitionGroup>
    );

    useEffect(() => {
        if(gamesState === status.IDLE && leagueState === status.COMPLETE && gamesState !== status.ERROR) {
            let request = new SeasonRequest(currSeason, currSeasonType, currWeek);
            dispatch(fetchWeek(request));
        }
    }, [gamesState, leagueState, currSeason, currWeek, currSeasonType, week, dispatch])

    useEffect(() => {
        if((season && week && seasonType) && week !== setWeek && gamesState !== status.IDLE && gamesState !== status.ERROR) {
            let request = new SeasonRequest(season, seasonType, week);
            dispatch(fetchWeek(request));
        }
    }, [season, week, seasonType, other, setWeek, gamesState, dispatch]);

    useEffect(() => {
        window.scrollTo(0,0);
    },[]);

    return (
        <div {...swipeHandlers} style={{ touchAction: 'pan-y' }}>
            { spectatorView }
            { gamesTab }
            <div className="games-week-container">
                <div className="week-switcher-container">
                    <WeekSwitcher 
                        league={league}
                        filterList={filterList}
                    />
                </div>
                { transitionGroup }
            </div>
            <PickPeekModal />
        </div>
    );
}